import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTechnologies, useAllTags, useTechnologyProductFamilies } from '../../graphql/queries';
import {
  DataTable,
  StatusBadge,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
  type SortDirection,
} from '../../components/shared';
import { EntityAutocompleteField } from '../../components/forms/EntityAutocompleteField';
import {
  SupplierType,
  TechnologyLifecycleStage,
  type Technology,
  type TechnologyFilter,
} from '../../graphql/types';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import './index.css';

export const Route = createFileRoute('/technologies/')({
  component: TechnologiesListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
    // Deep-link from the Suppliers card's technology-product-count link
    // (issue #1256) — mirrors the tags list's `categoryId` URL param.
    supplierId: typeof search.supplierId === 'string' ? search.supplierId : '',
    // Deep-link support for the Product Family card's technologyProductCount
    // link (issue #1257) — pre-selects the productFamilyId dropdown below
    // and survives a refresh/share since it round-trips through the URL,
    // mirroring the Tags list's categoryId precedent (issue #715).
    productFamilyId: typeof search.productFamilyId === 'string' ? search.productFamilyId : '',
  }),
});

export { TechnologiesListPage };

interface TechnologyFilters {
  search: string;
  productFamilyId: string;
  lifecycleStage: TechnologyLifecycleStage | '';
  supplierType: SupplierType | '';
  tagId: string;
}

/** Human-readable labels for the ownership/origin filter (issue #564, UJ-TECH-17). */
const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  [SupplierType.Internal]: 'Internal',
  [SupplierType.Vendor]: 'Vendor',
  [SupplierType.OpenSource]: 'Open Source',
  [SupplierType.Partner]: 'Partner',
};

// Sorted alphabetically by display label (issue #1865) — the enum's
// declaration order (Adopt, Hold, Evaluate, Sunset) doesn't match the
// alphabetical order users scan for (Adopt, Evaluate, Hold, Sunset).
const SORTED_LIFECYCLE_STAGES = Object.values(TechnologyLifecycleStage).sort((a, b) =>
  a.localeCompare(b)
);

// Sorted alphabetically by display label, not enum declaration order
// (issue #1865, mirrors the lifecycle-stage sort above).
const SORTED_SUPPLIER_TYPES = Object.values(SupplierType).sort((a, b) => {
  // a/b are fixed SupplierType enum values — not user-controlled
  // eslint-disable-next-line security/detect-object-injection
  return SUPPLIER_TYPE_LABELS[a].localeCompare(SUPPLIER_TYPE_LABELS[b]);
});

const ForgeButton = 'forge-button' as React.ElementType;
const ForgeTextField = 'forge-text-field' as React.ElementType;
const ForgeSelect = 'forge-select' as React.ElementType;
const ForgeOption = 'forge-option' as React.ElementType;

function TechnologiesListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize, supplierId, productFamilyId: productFamilyIdParam } = Route.useSearch();

  // productFamilyId seeds from the URL (issue #1257 deep link from the
  // Product Family card); every other filter stays local-only, as before.
  const [filters, setFilters] = useState<TechnologyFilters>({
    search: '',
    productFamilyId: productFamilyIdParam ?? '',
    lifecycleStage: '',
    supplierType: '',
    tagId: '',
  });

  const [sortColumn, setSortColumn] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // useAllTags (not the capped useTags(), issue #1484) — a tag catalog past
  // the resolver's 50-item default page would otherwise silently drop from
  // this filter dropdown, mirroring the fix already applied to TagPicker /
  // ServiceTagsAndCustomFields / BusinessEnvironmentFactorDetailPage (#1352).
  const { tags: allTags } = useAllTags();
  const { data: familiesData } = useTechnologyProductFamilies();

  // Sorted, searchable option lists for the two data-driven, unbounded
  // filters (issue #1865) — built once per data change rather than inline
  // in the JSX so `EntityAutocompleteField`'s `options` identity stays
  // stable across renders that don't touch the underlying catalog.
  const productFamilyOptions = useMemo(
    () =>
      [...(familiesData?.technologyProductFamilies ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((f) => ({ value: f.entityId, label: f.name })),
    [familiesData]
  );
  const tagOptions = useMemo(
    () =>
      [...allTags]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({
          value: t.entityId,
          label: t.name,
        })),
    [allTags]
  );

  const technologyFilter: TechnologyFilter | undefined = (() => {
    const f: TechnologyFilter = {};
    if (filters.search) f.search = filters.search;
    if (filters.productFamilyId) f.productFamilyId = filters.productFamilyId;
    if (filters.lifecycleStage) f.lifecycleStage = filters.lifecycleStage;
    if (filters.supplierType) f.supplierType = filters.supplierType;
    if (filters.tagId) f.tagIds = [filters.tagId];
    // Deep-linked from the Suppliers card (issue #1256) — URL-driven, not a
    // visible filter control on this page (see UJ-TECH-1256).
    if (supplierId) f.supplierId = supplierId;
    return Object.keys(f).length > 0 ? f : undefined;
  })();

  const { data, loading, error } = useTechnologies({
    filter: technologyFilter,
    page,
    pageSize,
    sortField: sortColumn,
    sortDirection,
  });

  const technologies = data?.technologies?.edges?.map((edge) => edge.node) ?? [];
  const totalCount = data?.technologies?.totalCount ?? 0;

  const handleSort = (column: string, direction: SortDirection): void => {
    setSortColumn(column);
    setSortDirection(direction);
    void navigate({
      to: '/technologies',
      search: { page: 1, pageSize, supplierId, productFamilyId: filters.productFamilyId },
    });
  };

  const columns: DataTableColumn<Technology>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '25%',
      render: (tech) => (
        <div>
          <div className="entity-list-name-cell">{tech.name}</div>
          {tech.version && <div className="entity-list-description-cell">v{tech.version}</div>}
        </div>
      ),
    },
    {
      key: 'productFamilyId',
      label: 'Product Family',
      sortable: true,
      width: '15%',
      render: (tech) => <span>{tech.productFamilyId ?? '—'}</span>,
    },
    {
      key: 'lifecycleStage',
      label: 'Lifecycle',
      sortable: true,
      width: '15%',
      render: (tech) => <StatusBadge variant="lifecycleStage" value={tech.lifecycleStage} />,
    },
    {
      key: 'supplierId',
      label: 'Supplier',
      sortable: true,
      width: '15%',
      render: (tech) => tech.supplierId || '—',
    },
    {
      key: 'description',
      label: 'Description',
      width: '15%',
      render: (tech) => (
        <div className="entity-list-truncate-cell--fixed">{tech.description || '—'}</div>
      ),
    },
  ];

  const handleRowClick = (tech: Technology): void => {
    void navigate({ to: '/technologies/$technologyId', params: { technologyId: tech.entityId } });
  };

  const handleFilterChange = (newFilters: Partial<TechnologyFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    // productFamilyId round-trips through the URL (issue #1257) so the
    // Product Family card's link stays refresh-safe/shareable; other
    // filters remain local-only, unchanged from before.
    const nextProductFamilyId = newFilters.productFamilyId ?? filters.productFamilyId;
    void navigate({
      to: '/technologies',
      search: { page: 1, pageSize, supplierId, productFamilyId: nextProductFamilyId },
    });
  };

  const handleClearFilters = (): void => {
    setFilters({
      search: '',
      productFamilyId: '',
      lifecycleStage: '',
      supplierType: '',
      tagId: '',
    });
    // Clearing filters also drops the Suppliers-card deep link (#1256) and
    // the Product Family card deep link (#1257).
    void navigate({
      to: '/technologies',
      search: { page: 1, pageSize, supplierId: '', productFamilyId: '' },
    });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({
      to: '/technologies',
      search: { page: newPage, pageSize, supplierId, productFamilyId: filters.productFamilyId },
    });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({
      to: '/technologies',
      search: {
        page: 1,
        pageSize: newPageSize,
        supplierId,
        productFamilyId: filters.productFamilyId,
      },
    });
  };

  const hasActiveFilters =
    filters.search ||
    filters.productFamilyId ||
    filters.lifecycleStage ||
    filters.supplierType ||
    filters.tagId ||
    supplierId;

  if (error) {
    return (
      <div className="technologies-list-page">
        <h1 className="forge-typography--headline4">Technologies</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load technologies"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="technologies-list-page">
      <div className="page-header page-header--toolbar page-header--spaced">
        <div className="page-header-title">
          <h1 className="forge-typography--headline4">Technologies</h1>
          <span className="forge-typography--body2 page-header-count">
            {loading ? 'Loading...' : `${totalCount} technologies`}
          </span>
        </div>
        <PermissionGate permission="technology:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => void navigate({ to: '/technologies/new' })}
          >
            Create Technology
          </ForgeButton>
        </PermissionGate>
      </div>

      <div className="filters-row">
        <div className="search-field">
          {/* forge-text-field renders no input of its own — it only reads a
              slotted light-DOM <input> (see @tylertech/forge text-field-adapter
              `_inputElements = assignedElements.filter(...)`). Setting
              value/onChange/placeholder on the outer element alone is a no-op;
              the real input goes inside it (issue #564). */}
          <ForgeTextField>
            <input
              name="search"
              type="text"
              placeholder="Search technologies..."
              aria-label="Search technologies"
              value={filters.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFilterChange({ search: e.target.value })
              }
            />
          </ForgeTextField>
        </div>
        <div className="filter-selects">
          {/* Data-driven, unbounded catalog — sorted + searchable via
              EntityAutocompleteField rather than a plain forge-select
              (issue #1865). Reports selection via a `forge-autocomplete-change`
              custom event, not a native `change` event, so `onChange` calls
              `handleFilterChange` directly with the resolved entity id. */}
          <EntityAutocompleteField
            name="productFamilyId"
            label="Product Family"
            options={productFamilyOptions}
            value={filters.productFamilyId}
            onChange={(id) => handleFilterChange({ productFamilyId: id })}
            placeholder="All Product Families"
            wrapperClassName="filter-autocomplete-field"
          />

          <ForgeSelect
            name="lifecycleStage"
            label="Lifecycle Stage"
            value={filters.lifecycleStage}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange({
                lifecycleStage: e.target.value as TechnologyLifecycleStage | '',
              })
            }
          >
            <ForgeOption value="">All Lifecycle Stages</ForgeOption>
            {SORTED_LIFECYCLE_STAGES.map((stage) => (
              <ForgeOption key={stage} value={stage}>
                {stage}
              </ForgeOption>
            ))}
          </ForgeSelect>

          <ForgeSelect
            name="supplierType"
            label="Supplier Type"
            value={filters.supplierType}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange({ supplierType: e.target.value as SupplierType | '' })
            }
          >
            <ForgeOption value="">All Supplier Types</ForgeOption>
            {SORTED_SUPPLIER_TYPES.map((type) => {
              // type is a fixed SupplierType enum value — not user-controlled
              // eslint-disable-next-line security/detect-object-injection
              const label = SUPPLIER_TYPE_LABELS[type];
              return (
                <ForgeOption key={type} value={type}>
                  {label}
                </ForgeOption>
              );
            })}
          </ForgeSelect>

          {/* Data-driven, unbounded catalog — sorted + searchable, same
              rationale as the Product Family field above (issue #1865). */}
          <EntityAutocompleteField
            name="tagId"
            label="Tags"
            options={tagOptions}
            value={filters.tagId}
            onChange={(id) => handleFilterChange({ tagId: id })}
            placeholder="All Tags"
            wrapperClassName="filter-autocomplete-field"
          />

          {hasActiveFilters && (
            <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
              Clear Filters
            </ForgeButton>
          )}
        </div>
      </div>

      <div className="technologies-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={6} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={technologies}
              getRowKey={(tech) => tech.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'memory',
                title: 'No technologies found',
                description: hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Create your first technology to get started',
                actionLabel: 'Create Technology',
                onAction: () => void navigate({ to: '/technologies/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
